import { useMemoizedFn, useSetState } from 'ahooks';
import moment from 'moment';
import { useEffect, useMemo, useRef } from 'react';
import { useRequestController } from '@sg/hooks/useRequestController';
import { ALL_SYSTEMS_PLATFORM } from '../utils/liveTitleOsFilter';
import type { ILiveTitleOsGroup } from '../utils/liveTitleOsFilter';

export { ALL_SYSTEMS_PLATFORM };

/** Shared ahooks cache key for global cond (`live_title_studio_game: null`). */
export const LIVE_TITLE_COND_GLOBAL_CACHE_KEY = '/api/v1/globalpedia_pc/condPOST';

export interface ILiveTitleCondData {
  live_title_os?: ILiveTitleOsGroup[];
  live_title_country_code?: unknown;
  live_title_studio_game?: unknown;
}

/** Cond fetch for Live Titles filters. Pass null gameTitle for the global platform/title list. */
export const useLiveTitleCond = (
  gameTitle: string | null | undefined,
  options?: { readyWhenEmpty?: boolean },
) => {
  const readyWhenEmpty = Boolean(options?.readyWhenEmpty);
  const condPayload = useMemo(
    () => ({
      live_title_country_code: null,
      live_title_os: null,
      live_title_studio_game: gameTitle ?? null,
    }),
    [gameTitle],
  );

  return useRequestController({
    method: 'POST',
    url: '/api/v1/globalpedia_pc/cond',
    refreshDeps: condPayload,
    ready: Boolean(gameTitle) || readyWhenEmpty,
    cacheKey: gameTitle
      ? `live-title-cond-${gameTitle}`
      : LIVE_TITLE_COND_GLOBAL_CACHE_KEY,
    // Global list is stable; game-scoped list must refetch on title switch.
    staleTime: gameTitle ? 0 : 300000,
  });
};

/**
 * Resets platform selection when the active game/title changes.
 * Live Visualization passes `studio.name`; Live Download passes a single selected title.
 * Callers usually pass `() => handleComparisonChange([ALL_SYSTEMS_PLATFORM])` so platform
 * options (game-scoped via cond API) don't carry over from the previous game.
 * Skips the first mount so the initial `['All Systems']` default is kept.
 */
export const useResetPlatformsOnGameChange = (
  gameTitleKey: string | null | undefined,
  onReset: () => void,
) => {
  const prevKeyRef = useRef<string | undefined>();

  useEffect(() => {
    const nextKey = gameTitleKey ?? '';
    // Only reset after the first render, when user switches to a different game.
    if (prevKeyRef.current !== undefined && prevKeyRef.current !== nextKey) {
      onReset();
    }
    prevKeyRef.current = nextKey;
  }, [gameTitleKey, onReset]);
};

export const useLivPageTrigger = () => {
  // 使用useSetState统一管理所有状态
  const [state, setState] = useSetState({
    selectedTitle: '',
    selectedPlatforms: [ALL_SYSTEMS_PLATFORM],
    selectedRegion: {
      region_type: 'market',
      markets: ['255'],
      regions: [],
    },
    selectedDate: [
      moment().subtract(13, 'month').startOf('month'),
      moment().subtract(1, 'months').endOf('month'),
    ],
    selectStudio: [],
    selectYM: ['1', '2']
  });

  // 使用useMemoizedFn包装统一的状态更新方法
  const handleStateChange = useMemoizedFn((name: keyof typeof state, value: any) => {
    setState({
      [name]: value
    } as any);
  });

  // 保留原有的方法别名，同样使用useMemoizedFn
  const handleTitleChange = useMemoizedFn((title: string) => {
    handleStateChange('selectedTitle', title);
  });

  const handleComparisonChange = useMemoizedFn((values: string[]) => {
    handleStateChange('selectedPlatforms', values);
  });

  const handleRegionChange = useMemoizedFn((region: any) => {
    handleStateChange('selectedRegion', region);
  });

  const handleDateChange = useMemoizedFn((date: any) => {
    handleStateChange('selectedDate', date);
  });

  const handleStudioChange = useMemoizedFn((value: string[]) => {
    handleStateChange('selectStudio', value);
  });

  const handleYMChange = useMemoizedFn((value: string[]) => {
    handleStateChange('selectYM', value);
  });

  return {
    ...state,
    handleStateChange,
    handleTitleChange,
    handleComparisonChange,
    handleRegionChange,
    handleDateChange,
    handleStudioChange,
    handleYMChange,
  };
};
